import type {
    LibRawImageData,
    LibRawMetadata,
    LibRawSettings,
    LibRawThumbnailData,
} from 'libraw-wasm';

export type RawDevelopSettings = LibRawSettings;

export const DEFAULT_RAW_DEVELOP_SETTINGS: RawDevelopSettings = {
    bright: 1,
    threshold: 0,
    autoBrightThr: 0.01,
    adjustMaximumThr: 0.75,
    expShift: 1,
    expPreser: 0,
    halfSize: false,
    fourColorRgb: false,
    highlight: 0,
    useAutoWb: false,
    useCameraWb: true,
    useCameraMatrix: 1,
    outputColor: 1,
    outputBps: 8,
    userFlip: -1,
    userQual: 3,
    userBlack: -1,
    userCblack: [-1, -1, -1, -1],
    userSat: 0,
    medPasses: 0,
    noAutoBright: false,
    useFujiRotate: -1,
    greenMatching: false,
    dcbIterations: -1,
    dcbEnhanceFl: false,
    fbddNoiserd: 0,
    expCorrec: false,
    noAutoScale: false,
    noInterpolation: false,
};

export const RAW_EXTENSIONS = [
    'cr3', 'cr2', 'crw',
    'arw', 'sr2', 'srf',
    'nef', 'nrw',
    'raf',
    'rw2', 'raw',
    'orf',
    'pef',
    'dng',
    'x3f',
    'iiq', '3fr',
] as const;

type WorkerResponse = {
    id: number;
    out?: unknown;
    error?: string;
};

class LibRawBrowserDecoder {
    private worker: Worker;
    private requestId = 0;
    private disposed = false;
    private pending = new Map<number, {
        resolve: (value: any) => void;
        reject: (reason: Error) => void;
        timeout: ReturnType<typeof setTimeout>;
    }>();

    constructor() {
        const baseUrl = process.env.BASE_URL || '/';
        const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
        this.worker = new Worker(`${normalizedBaseUrl}vendor/libraw-wasm/worker.js`, {
            type: 'module',
        });
        this.worker.onmessage = ({ data }: MessageEvent<WorkerResponse>) => {
            const request = this.pending.get(data.id);
            if (!request) return;

            clearTimeout(request.timeout);
            this.pending.delete(data.id);
            if (data.error) {
                request.reject(new Error(data.error));
            } else {
                request.resolve(data.out);
            }
        };
        this.worker.onerror = (event) => {
            event.preventDefault();
            this.dispose(new Error(event.message || 'Nie udało się uruchomić konwertera RAW.'));
        };
        this.worker.onmessageerror = () => this.dispose(new Error('Nie udało się odczytać odpowiedzi konwertera RAW.'));
    }

    public open(data: Uint8Array, settings: LibRawSettings): Promise<void> {
        return this.call('open', [data, settings], [data.buffer]);
    }

    public imageData(): Promise<LibRawImageData | undefined> {
        return this.call('imageData', []);
    }

    public metadata(): Promise<LibRawMetadata | undefined> {
        return this.call('metadata', [false]);
    }

    public thumbnailData(): Promise<LibRawThumbnailData | undefined> {
        return this.call('thumbnailData', []);
    }

    public dispose(error = new Error('Konwerter RAW został zatrzymany.')) {
        if (this.disposed) return;
        this.disposed = true;
        this.worker.onmessage = this.worker.onerror = this.worker.onmessageerror = null;
        this.worker.terminate();
        this.pending.forEach(({ reject, timeout }) => { clearTimeout(timeout); reject(error); });
        this.pending.clear();
    }

    private call<T>(fn: string, args: unknown[], transfer: Transferable[] = []): Promise<T> {
        if (this.disposed) return Promise.reject(new Error('Konwerter RAW został zatrzymany.'));
        const id = this.requestId++;
        return new Promise<T>((resolve, reject) => {
            const timeout = setTimeout(() => this.dispose(new Error('Przekroczono czas konwersji RAW.')), 300000);
            this.pending.set(id, { resolve, reject, timeout });
            try { this.worker.postMessage({ id, fn, args }, transfer); }
            catch (error) { this.dispose(error instanceof Error ? error : new Error(String(error))); }
        });
    }
}

export function isRawFile(file: File): boolean {
    const extension = file.name.split('.').pop()?.toLowerCase();
    return !!extension && (RAW_EXTENSIONS as readonly string[]).includes(extension);
}

export async function hasEmbeddedJpegPreview(file: File): Promise<boolean> {
    const decoder = new LibRawBrowserDecoder();

    try {
        const bytes = new Uint8Array(await file.arrayBuffer());
        await decoder.open(bytes, DEFAULT_RAW_DEVELOP_SETTINGS);
        const thumbnail = await decoder.thumbnailData();

        return thumbnail?.format === 'jpeg' && thumbnail.data.length > 0;
    } finally {
        decoder.dispose();
    }
}

export async function convertRawToJpeg(
    file: File,
    settings: RawDevelopSettings = DEFAULT_RAW_DEVELOP_SETTINGS,
    forceDevelop = false,
): Promise<File> {
    const blob = await developRawToJpegBlob(file, settings, forceDevelop);
    const baseName = file.name.replace(/\.[^/.]+$/, '');

    return new File([blob], `${baseName}.jpg`, {
        type: 'image/jpeg',
        lastModified: file.lastModified,
    });
}

export async function createRawPreview(
    file: File,
    settings: RawDevelopSettings,
): Promise<Blob> {
    return developRawToJpegBlob(file, {
        ...settings,
        halfSize: true,
        outputBps: 8,
        outputColor: settings.outputColor === 0 ? 1 : settings.outputColor,
        noInterpolation: false,
    }, true, 0.88);
}

async function developRawToJpegBlob(
    file: File,
    settings: RawDevelopSettings,
    forceDevelop: boolean,
    quality = 0.95,
): Promise<Blob> {
    const decoder = new LibRawBrowserDecoder();

    try {
        const bytes = new Uint8Array(await file.arrayBuffer());
        await decoder.open(bytes, { ...DEFAULT_RAW_DEVELOP_SETTINGS, ...settings });

        const metadata = await decoder.metadata();
        let blob: Blob;

        const thumbnail = forceDevelop ? undefined : await decoder.thumbnailData();
        if (!forceDevelop && thumbnail?.format === 'jpeg' && thumbnail.data.length > 0) {
            decoder.dispose();
            const preview = new Blob([thumbnail.data], { type: 'image/jpeg' });
            blob = metadata?.flip
                ? await rotateJpegPreview(preview, metadata.flip)
                : preview;
        } else {
            const image = await decoder.imageData();
            decoder.dispose();
            if (!image || !image.width || !image.height || !image.data) {
                throw new Error('Plik RAW nie zawiera obrazu możliwego do przekonwertowania.');
            }
            blob = await imageDataToJpeg(image, quality);
        }

        return blob;
    } finally {
        decoder.dispose();
    }
}

async function rotateJpegPreview(blob: Blob, flip: number): Promise<Blob> {
    const bitmap = await createImageBitmap(blob, { imageOrientation: 'none' });
    const swapsDimensions = (flip & 4) === 4;
    const canvas = document.createElement('canvas');
    canvas.width = swapsDimensions ? bitmap.height : bitmap.width;
    canvas.height = swapsDimensions ? bitmap.width : bitmap.height;

    try {
        const context = canvas.getContext('2d');
        if (!context) {
            throw new Error('Przeglądarka nie obsługuje korekty orientacji obrazu.');
        }

        if (flip === 1) {
            context.translate(canvas.width, 0);
            context.scale(-1, 1);
        } else if (flip === 2) {
            context.translate(0, canvas.height);
            context.scale(1, -1);
        } else if (flip === 3) {
            context.translate(canvas.width, canvas.height);
            context.rotate(Math.PI);
        } else if (flip === 4) {
            context.transform(0, 1, 1, 0, 0, 0);
        } else if (flip === 5) {
            context.translate(0, canvas.height);
            context.rotate(-Math.PI / 2);
        } else if (flip === 6) {
            context.translate(canvas.width, 0);
            context.rotate(Math.PI / 2);
        } else if (flip === 7) {
            context.transform(0, -1, -1, 0, canvas.width, canvas.height);
        }

        context.drawImage(bitmap, 0, 0);

        return await canvasToJpeg(canvas);
    } finally {
        bitmap.close();
        canvas.width = canvas.height = 0;
    }
}

async function imageDataToJpeg(image: LibRawImageData, quality = 0.95): Promise<Blob> {
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;

    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Przeglądarka nie obsługuje konwersji obrazu.');
    }

    const rgba = new Uint8ClampedArray(image.width * image.height * 4);
    const source = image.data;
    const colors = image.colors || 3;
    const maxValue = image.bits > 8 ? Math.pow(2, image.bits) - 1 : 255;
    const normalize = (value: number) => image.bits > 8
        ? Math.round(value * 255 / maxValue)
        : value;

    for (let sourceIndex = 0, targetIndex = 0; targetIndex < rgba.length; sourceIndex += colors, targetIndex += 4) {
        rgba[targetIndex] = normalize(source[sourceIndex]);
        rgba[targetIndex + 1] = normalize(source[sourceIndex + Math.min(1, colors - 1)]);
        rgba[targetIndex + 2] = normalize(source[sourceIndex + Math.min(2, colors - 1)]);
        rgba[targetIndex + 3] = 255;
    }

    context.putImageData(new ImageData(rgba, image.width, image.height), 0, 0);

    return canvasToJpeg(canvas, quality);
}

async function canvasToJpeg(canvas: HTMLCanvasElement, quality = 0.95): Promise<Blob> {
    try {
        return await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (blob) resolve(blob);
                else reject(new Error('Nie udało się zapisać przekonwertowanego pliku JPEG.'));
            }, 'image/jpeg', quality);
        });
    } finally { canvas.width = canvas.height = 0; }
}
